import { inject, singleton } from 'tsyringe';
import { INotificationDisplayDetails } from 'app-core/src/models/notification-display-details.model';
import { LoggerService } from './logger.service';
import { DisplayedNotificationDetails } from './persistent-storage-service/models/displayed-notifications';
import { PersistentStorageService } from './persistent-storage-service/persistent-storage.service';
import { Application } from '../models/application.model';
import { Game } from '../models/game.model';
import { RecommendationDatastoreService } from './datastore-service/recommendation-datastore.service';
import {
  kDIConfigurations,
  kDITOWExtensionWrapper,
  kDITOWSettingsWrapper,
  kDITOWWrapper,
} from 'app-core/src/constants/injectionTokens';
import { IOWSettingsWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-settings-wrapper.interface';
import { IOWExtensionWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-extension-wrapper.interface';
import { IOWWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-wrapper.interface';
import { IConfigurations } from '../interfaces/configurations.interface';

// -----------------------------------------------------------------------------
@singleton()
export class NotificationDetailsBuilderService {
  private readonly logger = LoggerService.getCategory('NotificationDetails');

  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kDITOWSettingsWrapper)
    private readonly owSettingsWrapper: IOWSettingsWrapper,
    private readonly persistentStorage: PersistentStorageService,
    @inject(kDITOWWrapper)
    private readonly owWrapper: IOWWrapper,
    @inject(kDITOWExtensionWrapper)
    private readonly owExtensionWrapper: IOWExtensionWrapper,
    private readonly datastoreService: RecommendationDatastoreService,
    @inject(kDIConfigurations)
    private readonly configurations: IConfigurations,
  ) {}

  // ---------------------------------------------------------------------------
  public async getNotificationDetails(
    game: Game,
    application: Application,
    useDevEnv: string,
  ) {
    return this.createNotificationDetails(
      game,
      application,
      await this.buildUrlWithParameters(game, application, useDevEnv),
    );
  }

  // ---------------------------------------------------------------------------
  private createNotificationDetails(
    game: Game,
    application: Application,
    url: string,
  ): INotificationDisplayDetails {
    return {
      targetGameId: game.targetGameId || null,
      gameId: game.id,
      applicationUid: application.uid,
      url,
    };
  }

  // ---------------------------------------------------------------------------
  private async buildUrlWithParameters(
    game: Game,
    application: Application,
    useDevEnv: string,
  ): Promise<string> {
    try {
      const installedApps =
        await this.owExtensionWrapper.getInstalledAndEnabledExtensionUids();
      const lang = await this.owSettingsWrapper.getLanguage();
      const cooldown = await this.getCooldownFromLastConfig();
      const displayedInfo = this.getDisplayedInfo(game, application);
      const appVersion = await this.getAppVersion();
      const targetGameId = this.selectedTargetGameId(game);
      const timestamp = Date.now();
      const notificationIframeURL =
        this.configurations.generalConfiguration.notificationIframeURL;

      const iframeURL = new URL(notificationIframeURL);
      iframeURL.searchParams.append('gameId', targetGameId.toString());
      iframeURL.searchParams.append('extId', application.uid);
      iframeURL.searchParams.append('views', displayedInfo.count.toString());
      iframeURL.searchParams.append(
        'version',
        await this.owWrapper.getVersion(),
      );
      iframeURL.searchParams.append('appVersion', appVersion);
      iframeURL.searchParams.append('lang', lang);
      iframeURL.searchParams.append('cooldown', cooldown);
      iframeURL.searchParams.append('installedApps', installedApps.toString());
      iframeURL.searchParams.append('timestamp', timestamp.toString());
      iframeURL.searchParams.append('platform', this.owWrapper?.getPlatform());
      iframeURL.searchParams.append(
        'hostAppUid',
        this.owWrapper.getHostAppUid(),
      );

      if (useDevEnv) {
        iframeURL.pathname = iframeURL.pathname.replace('prod', useDevEnv);
        iframeURL.searchParams.append('localeEnv', useDevEnv);
      }

      if (Object.keys(application?.aliasApplicationDetails || {}).length > 0) {
        iframeURL.searchParams.append(
          'aliasApplicationDetails',
          JSON.stringify(application.aliasApplicationDetails),
        );
      }

      return iframeURL.toString();
    } catch (e) {
      this.logger.error('Failed to gather parameters, exception details:', e);
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  private selectedTargetGameId(game: Game) {
    return game.targetGameId ? game.targetGameId : game.id;
  }

  // ---------------------------------------------------------------------------
  private async getCooldownFromLastConfig() {
    const configs = await this.datastoreService.getGlobalConfigurations();
    const cooldown = configs.cooldownFromPreviousNotificationInHours.toString();
    return cooldown;
  }

  // ---------------------------------------------------------------------------
  private getDisplayedInfo(game: Game, application: Application) {
    let displayedInfo = this.persistentStorage.getDisplayedNotificationInfo(
      game,
      application,
    );

    if (!displayedInfo) {
      displayedInfo = {} as DisplayedNotificationDetails;
      displayedInfo.count = 0;
    }

    return displayedInfo;
  }

  // ---------------------------------------------------------------------------
  private async getAppVersion() {
    await this.owExtensionWrapper.initialize();
    const appVersion = this.owExtensionWrapper?.manifest?.meta?.version;
    return appVersion;
  }
}
